import { stopPropagation } from '@/_shared/tools/event-prevention.js';
import { TableRow, mergeClasses } from '@fluentui/react-components';
import _ from 'lodash';
import { type HTMLAttributes, type MouseEvent, memo, useCallback } from 'react';
import type { ContextModel } from '../../hooks/_shared/context.js';
import { Cell } from '../cell/index.js';
import { ResizableTableDividerBottom } from '../divider-bottom/index.js';
import { getRowIds } from '../getSelectedRowIds.js';
import { useStyles } from './styles.js';

export const ResizableTableRow = memo(_ResizableTableRow, (prev, next) => {
    const prevIndex = prev['data-index'];
    const nextIndex = next['data-index'];

    const prevRow = prev.context?.rows[prevIndex];
    const nextRow = next.context?.rows[nextIndex];

    const prevNextRow = prev.context?.rows.at(prevIndex + 1);
    const nextNextRow = next.context?.rows.at(nextIndex + 1);

    return (
        _.isEqual(prevRow?.original, nextRow?.original) &&
        _.isEqual(prevNextRow?.original, nextNextRow?.original) &&
        // _.isEqual(prevRow?.columnFilters, nextRow?.columnFilters) &&
        // _.isEqual(prevNextRow?.columnFilters, nextNextRow?.columnFilters) &&
        _.isEqual(prev.context?.columnOrder, next.context?.columnOrder) &&
        _.isEqual(prev.context?.columnSizes, next.context?.columnSizes) &&
        _.isEqual(prev.context?.columnVisibility, next.context?.columnVisibility) &&
        _.isEqual(
            prevRow?.id && getRowIds(prev.context?.rowSelection ?? {}).includes(prevRow.id),
            nextRow?.id && getRowIds(next.context?.rowSelection ?? {}).includes(nextRow.id)
        ) &&
        _.isEqual(
            prevRow?.id && prev.context?.rowSelectionHighlights?.includes(prevRow.id),
            nextRow?.id && next.context?.rowSelectionHighlights?.includes(nextRow.id)
        )
    );
}) as typeof _ResizableTableRow;

function _ResizableTableRow<TData>(
    props: HTMLAttributes<HTMLTableRowElement> & {
        'data-index': number;
        context?: ContextModel<TData>;
    }
) {
    const styles = useStyles();

    const index = props['data-index'];

    const row = props.context?.rows.at(index);
    const padContainerValue = props.context?.padContainerValue;
    const columnSizes = props.context?.columnSizes;

    const onClick = useCallback(
        (event: MouseEvent<HTMLTableRowElement>) => {
            stopPropagation(event);

            row?.getToggleSelectedHandler()(event);
            props.onClick?.(event);
        },
        [row, props.onClick]
    );

    if (!row || !columnSizes) {
        return null;
    }

    const cells = row.getVisibleCells();
    const shouldHighlight = props.context?.rowSelectionHighlights?.includes(row.id);

    return (
        <>
            <TableRow
                {...props}
                onClick={onClick}
                className={mergeClasses(
                    styles.row,
                    row.depth !== 0 && styles.rowNested,
                    (shouldHighlight || row.getIsSelected()) && styles.rowActive
                )}
                style={{
                    ...props.style,
                }}
            >
                {cells?.map((cell, index) => {
                    const isFirstColumn = index === 0;
                    const isLastColumn = index === cells.length - 1;

                    return (
                        <Cell
                            key={cell.id}
                            cell={cell}
                            padContainerValue={padContainerValue}
                            columnSizes={columnSizes}
                            position={isFirstColumn ? 'first' : isLastColumn ? 'last' : undefined}
                        />
                    );
                })}
            </TableRow>

            <ResizableTableDividerBottom size={padContainerValue ?? 0} />
        </>
    );
}
